package com.fantasytenis.web.screens.lineup

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.lineup.entity.LineupSlots
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.GuidedTourStep
import com.fantasytenis.web.components.GuidedTooltip
import com.fantasytenis.web.components.LineupSubmitConfirmationDialog
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.components.TooltipPosition
import com.fantasytenis.web.components.rememberGuidedTourState
import com.fantasytenis.web.utils.WebPreferences
import com.fantasytenis.web.components.lineup.MatchupBadge
import com.fantasytenis.web.components.lineup.MatchupIndicator
import com.fantasytenis.web.components.lineup.MobileSlotCarousel
import com.fantasytenis.web.components.lineup.PlayerCard
import com.fantasytenis.web.components.lineup.PlayerSkillDrawer
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.isMobile
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderBottom
import com.fantasytenis.web.theme.textTransform
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent

/**
 * Sort options for player list
 */
private enum class PlayerSortOption(val label: String) {
    BEST_VALUE("Best Value"),
    RANKING("Ranking"),
    COST_LOW("Cost: Low to High"),
    COST_HIGH("Cost: High to Low"),
    MATCHUP_EASY("Easiest Matchup")
}

/**
 * Lineup Builder State - Dashboard Mode
 *
 * Key change: No modal! Uses "selecting mode" where user clicks a slot,
 * then clicks a player card to add them.
 */
private data class LineupBuilderState(
    val players: List<TennisPlayerEntity> = emptyList(),
    val slots: LineupSlots = LineupSlots(),
    val totalCost: Int = 0,
    val remainingBudget: Int = 35,
    val salaryCap: Int = 35,
    val isValid: Boolean = false,
    val isLoading: Boolean = false,
    val isSubmitting: Boolean = false,
    val errors: List<String> = emptyList(),
    // Selecting mode: which category slot is being filled (null = browse mode)
    val selectingCategory: Category? = null,
    val showSubmitConfirmation: Boolean = false,
    val contestName: String = "",
    val slateGender: String = "M",
    // Search and sort
    val searchQuery: String = "",
    val sortOption: PlayerSortOption = PlayerSortOption.BEST_VALUE,
    val showAffordableOnly: Boolean = true,
    // Success feedback
    val lastAddedPlayer: String? = null,
    val showSuccessToast: Boolean = false,
    // Skill drawer
    val viewingSkillsPlayer: TennisPlayerEntity? = null,
    // Edit mode state
    val editEntryId: String? = null,
    val editLineupId: String? = null
) {
    val isEditMode: Boolean get() = editLineupId != null
}

/**
 * Lineup Builder Screen
 *
 * Allows users to build a 5-slot lineup by selecting players for each category.
 * Includes salary cap tracking and validation.
 */
@Composable
fun LineupBuilderScreen(
    contestId: String,
    entryId: String? = null,
    lineupId: String? = null,
    onSubmit: () -> Unit = {},
    onBack: () -> Unit = {}
) {
    var state by remember { mutableStateOf(LineupBuilderState()) }
    val scope = rememberCoroutineScope()
    val isMobileView = isMobile()

    // First-time user guided tour
    val isFirstLineup = remember { !WebPreferences.hasCompletedFirstLineup() }
    val guidedTourSteps = remember {
        listOf(
            GuidedTourStep(
                id = "serve",
                title = "Serve Category",
                description = "Pick a player with a strong serve. Earn +3 points for aces, -1 for double faults.",
                position = TooltipPosition.BOTTOM,
                accentColor = Colors.Category.Serve
            ),
            GuidedTourStep(
                id = "forehand",
                title = "Forehand Category",
                description = "Pick a player with powerful forehands. Earn +2 for forehand winners, -1 for errors.",
                position = TooltipPosition.BOTTOM,
                accentColor = Colors.Category.Forehand
            ),
            GuidedTourStep(
                id = "backhand",
                title = "Backhand Category",
                description = "Pick a player with a solid backhand. Same scoring as forehand: +2 winners, -1 errors.",
                position = TooltipPosition.BOTTOM,
                accentColor = Colors.Category.Backhand
            ),
            GuidedTourStep(
                id = "volleys",
                title = "Volleys Category",
                description = "Pick a player who excels at the net. Earn +3 for volley winners.",
                position = TooltipPosition.BOTTOM,
                accentColor = Colors.Category.Volleys
            ),
            GuidedTourStep(
                id = "fighting",
                title = "Fighting Spirit",
                description = "Pick a clutch player. Earn +4 for breaks converted, +2 for break points saved.",
                position = TooltipPosition.BOTTOM,
                accentColor = Colors.Category.Fighting
            ),
            GuidedTourStep(
                id = "budget",
                title = "Stay Under Budget",
                description = "Your 5 players must cost \$${state.salaryCap}K or less total. Mix stars with value picks!",
                position = TooltipPosition.TOP
            )
        )
    }
    val guidedTour = rememberGuidedTourState(
        steps = guidedTourSteps,
        onComplete = { WebPreferences.setFirstLineupCompleted() }
    )
    val showGuidedTour = isFirstLineup && !guidedTour.isComplete

    // Load contest and players on mount
    LaunchedEffect(contestId) {
        state = state.copy(isLoading = true, errors = emptyList())

        // First, get the contest to retrieve slateId and salary cap
        DependencyContainer.getContestUseCase(contestId).fold(
            onSuccess = { contest ->
                state = state.copy(
                    contestName = contest.name,
                    salaryCap = contest.salaryCap,
                    remainingBudget = contest.salaryCap
                )

                val slateId = contest.tournamentId

                // Try to get slate for gender, fallback to "M" if not available
                val gender = DependencyContainer.getSlateUseCase(slateId).fold(
                    onSuccess = { slate -> slate.gender },
                    onFailure = { "M" }  // Default to ATP if slate fetch fails
                )
                state = state.copy(slateGender = gender)

                // Try slate players first, fallback to generic players
                DependencyContainer.getSlatePlayersUseCase(slateId, gender).fold(
                    onSuccess = { players ->
                        state = state.copy(players = players, isLoading = false)
                    },
                    onFailure = {
                        // Fallback to old endpoint if slate players not available
                        DependencyContainer.getPlayersUseCase(slateId).fold(
                            onSuccess = { players ->
                                state = state.copy(players = players, isLoading = false)
                            },
                            onFailure = { error ->
                                state = state.copy(
                                    errors = listOf(error.message ?: "Failed to load players"),
                                    isLoading = false
                                )
                            }
                        )
                    }
                )
            },
            onFailure = { error ->
                state = state.copy(
                    errors = listOf(error.message ?: "Failed to load contest"),
                    isLoading = false
                )
            }
        )
    }

    // Load existing lineup when editing
    LaunchedEffect(entryId, lineupId, state.players) {
        if (entryId != null && state.players.isNotEmpty()) {
            // Determine the lineupId - either passed directly or fetch from entry
            val actualLineupId = lineupId ?: run {
                // Fetch entry to get lineupId (handles URL refresh/bookmark case)
                DependencyContainer.getUserContestEntriesUseCase(contestId).fold(
                    onSuccess = { entries -> entries.find { it.id == entryId }?.lineupId },
                    onFailure = { null }
                )
            }

            // Store in state for later use during submit
            state = state.copy(editEntryId = entryId, editLineupId = actualLineupId)

            if (actualLineupId != null) {
                DependencyContainer.getLineupUseCase(actualLineupId).fold(
                    onSuccess = { lineup ->
                        val existingSlots = lineup.slots
                        val cost = calculateTotalCost(existingSlots, state.players)
                        state = state.copy(
                            slots = existingSlots,
                            totalCost = cost,
                            remainingBudget = state.salaryCap - cost,
                            isValid = existingSlots.isFilled() && (state.salaryCap - cost) >= 0
                        )
                    },
                    onFailure = { error ->
                        state = state.copy(
                            errors = state.errors + "Failed to load existing lineup: ${error.message}"
                        )
                    }
                )
            }
        }
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = if (state.isEditMode) "Edit Lineup" else "Build Your Lineup",
            showBackButton = true,
            onBackClick = onBack,
            customActions = {
                // Salary Cap Display (visible on desktop)
                SalaryCapBar(
                    used = state.totalCost,
                    remaining = state.remainingBudget,
                    cap = state.salaryCap
                )
            }
        )

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(1200.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            // Error Messages
            if (state.errors.isNotEmpty()) {
                Div({
                    style {
                        backgroundColor(Colors.ErrorLight)
                        borderRadius(8.px)
                        padding(16.px)
                        marginBottom(24.px)
                    }
                }) {
                    state.errors.forEach { error ->
                        P({
                            style {
                                color(Colors.ErrorDark)
                                margin(0.px)
                            }
                        }) {
                            Text(error)
                        }
                    }
                }
            }

            // First-time user guided tour banner
            if (showGuidedTour) {
                guidedTour.currentStep?.let { step ->
                    FirstTimeUserGuideBanner(
                        step = step,
                        currentStepIndex = guidedTour.currentStepIndex + 1,
                        totalSteps = guidedTour.totalSteps,
                        onNext = { guidedTour.nextStep() },
                        onSkip = { guidedTour.skipAll() }
                    )
                }
            }

            // Success Toast
            if (state.showSuccessToast && state.lastAddedPlayer != null) {
                LaunchedEffect(state.lastAddedPlayer) {
                    delay(2500)
                    state = state.copy(showSuccessToast = false)
                }
                Div({
                    style {
                        backgroundColor(Colors.Success)
                        color(Colors.White)
                        padding(12.px, 20.px)
                        borderRadius(8.px)
                        marginBottom(16.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                        fontWeight(500)
                    }
                }) {
                    Text("${state.lastAddedPlayer} added to ${state.selectingCategory?.displayName ?: "lineup"}")
                }
            }

            // Mobile: Sticky Selecting Mode Header (follows user as they scroll)
            // Desktop: Regular selecting mode header
            if (state.selectingCategory != null) {
                Div({
                    style {
                        backgroundColor(getCategoryColor(state.selectingCategory!!))
                        color(Colors.White)
                        padding(12.px, if (isMobileView) 16.px else 20.px)
                        borderRadius(8.px)
                        marginBottom(16.px)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        // On mobile, make it sticky so it follows scroll
                        if (isMobileView) {
                            position(Position.Sticky)
                            top(0.px)
                            property("z-index", "10")
                            borderRadius(0.px)
                            marginLeft((-24).px)
                            marginRight((-24).px)
                            marginTop((-24).px)
                            marginBottom(16.px)
                        }
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            flexWrap(FlexWrap.Wrap)
                        }
                    }) {
                        Span({
                            style {
                                fontSize(if (isMobileView) 14.px else 16.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("Select for ${state.selectingCategory!!.displayName}")
                        }
                        if (isMobileView) {
                            Span({
                                style {
                                    fontSize(12.px)
                                    opacity(0.9)
                                }
                            }) {
                                Text("$${state.remainingBudget}K left")
                            }
                        } else {
                            Span({
                                style {
                                    fontSize(13.px)
                                    opacity(0.9)
                                }
                            }) {
                                Text("Tap a player card below")
                            }
                        }
                    }
                    Span({
                        style {
                            cursor("pointer")
                            padding(8.px, 16.px)
                            backgroundColor(Color("rgba(255,255,255,0.2)"))
                            borderRadius(4.px)
                            fontSize(13.px)
                            minHeight(44.px) // Touch target
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                        }
                        onClick {
                            state = state.copy(selectingCategory = null)
                        }
                    }) {
                        Text("Cancel")
                    }
                }
            }

            // Mobile Budget Display (hidden on desktop, shown in header there)
            // Only show on desktop - mobile gets budget in carousel
            if (!isMobileView) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        backgroundColor(if (state.remainingBudget < 0) Colors.ErrorLight else Colors.Surface)
                        padding(12.px, 16.px)
                        borderRadius(8.px)
                        marginBottom(16.px)
                        if (state.remainingBudget < 0) {
                            border(2.px, LineStyle.Solid, Colors.Error)
                        }
                    }
                    classes("mobile-budget-display")
                }) {
                    Span({
                        style {
                            fontSize(14.px)
                            color(if (state.remainingBudget < 0) Colors.Error else Colors.TextSecondary)
                            fontWeight(if (state.remainingBudget < 0) 600 else 400)
                        }
                    }) {
                        Text(if (state.remainingBudget < 0) "Over Budget!" else "Budget")
                    }
                    Span({
                        style {
                            fontSize(16.px)
                            fontWeight(600)
                            color(if (state.remainingBudget < 0) Colors.Error else Colors.Primary)
                        }
                    }) {
                        Text("$${state.totalCost}K / $${state.salaryCap}K (${if (state.remainingBudget >= 0) "$${state.remainingBudget}K left" else "$${-state.remainingBudget}K over"})")
                    }
                }
            }

            // Over Budget Warning Banner
            if (state.remainingBudget < 0) {
                Div({
                    style {
                        backgroundColor(Colors.ErrorLight)
                        border(1.px, LineStyle.Solid, Colors.Error)
                        borderRadius(8.px)
                        padding(12.px, 16.px)
                        marginBottom(16.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(12.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(20.px)
                        }
                    }) {
                        Text("⚠️")
                    }
                    Div {
                        P({
                            style {
                                color(Colors.Error)
                                fontWeight(600)
                                fontSize(14.px)
                                margin(0.px, 0.px, 4.px, 0.px)
                            }
                        }) {
                            Text("Your lineup is $${-state.remainingBudget}K over the salary cap")
                        }
                        P({
                            style {
                                color(Colors.ErrorDark)
                                fontSize(13.px)
                                margin(0.px)
                            }
                        }) {
                            Text("Remove a player or choose a less expensive option to submit.")
                        }
                    }
                }
            }

            // Category Slots - Mobile: horizontal carousel, Desktop: grid
            if (isMobileView) {
                // Mobile: Use horizontal carousel
                MobileSlotCarousel(
                    slots = state.slots,
                    players = state.players,
                    selectingCategory = state.selectingCategory,
                    totalCost = state.totalCost,
                    salaryCap = state.salaryCap,
                    remainingBudget = state.remainingBudget,
                    onSlotClick = { category ->
                        // Toggle selecting mode for this category
                        val isSelectingThis = state.selectingCategory == category
                        state = if (isSelectingThis) {
                            state.copy(selectingCategory = null)
                        } else {
                            state.copy(selectingCategory = category)
                        }
                    },
                    onRemove = { category ->
                        val newSlots = state.slots.withSlot(category, null)
                        val newCost = calculateTotalCost(newSlots, state.players)
                        state = state.copy(
                            slots = newSlots,
                            totalCost = newCost,
                            remainingBudget = state.salaryCap - newCost,
                            isValid = newSlots.isFilled() && (state.salaryCap - newCost) >= 0
                        )
                    }
                )
            } else {
                // Desktop: Use grid layout
                Section({
                    style {
                        display(DisplayStyle.Grid)
                        // Responsive grid: auto-fit with min 150px per column
                        property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 150px), 1fr))")
                        gap(16.px)
                        marginBottom(24.px)
                    }
                    classes("lineup-slots-grid")
                }) {
                    Category.entries.forEach { category ->
                        val playerId = state.slots.getPlayerForCategory(category)
                        val player = playerId?.let { id ->
                            state.players.find { it.id == id }
                        }
                        val isSelectingThis = state.selectingCategory == category

                        CategorySlot(
                            category = category,
                            player = player,
                            isSelecting = isSelectingThis,
                            onClick = {
                                // Toggle selecting mode for this category
                                state = if (isSelectingThis) {
                                    state.copy(selectingCategory = null)
                                } else {
                                    state.copy(selectingCategory = category)
                                }
                            },
                            onRemove = if (player != null) {
                                {
                                    val newSlots = state.slots.withSlot(category, null)
                                    val newCost = calculateTotalCost(newSlots, state.players)
                                    state = state.copy(
                                        slots = newSlots,
                                        totalCost = newCost,
                                        remainingBudget = state.salaryCap - newCost,
                                        isValid = newSlots.isFilled() && (state.salaryCap - newCost) >= 0
                                    )
                                }
                            } else null
                        )
                    }
                }
            }

            // Action Buttons (Desktop only - mobile gets fixed button at bottom)
            if (!isMobileView) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        gap(16.px)
                        marginBottom(24.px)
                    }
                }) {
                    // Clear All button - only show if any slot is filled
                    if (state.slots.filledCount() > 0) {
                        Button(
                            text = "Clear All",
                            onClick = {
                                state = state.copy(
                                    slots = LineupSlots(),
                                    totalCost = 0,
                                    remainingBudget = state.salaryCap,
                                    isValid = false,
                                    selectingCategory = null
                                )
                            },
                            variant = "secondary"
                        )
                    }

                    Button(
                        text = when {
                            state.isSubmitting -> if (state.isEditMode) "Updating..." else "Submitting..."
                            state.isValid -> if (state.isEditMode) "Update Lineup" else "Submit Lineup"
                            state.remainingBudget < 0 -> "Over Budget by $${-state.remainingBudget}K"
                            state.slots.filledCount() < 5 -> "Complete All Slots (${state.slots.filledCount()}/5)"
                            else -> if (state.isEditMode) "Update Lineup" else "Submit Lineup"
                        },
                        onClick = {
                            if (state.isValid && !state.isSubmitting) {
                                state = state.copy(showSubmitConfirmation = true)
                            }
                        },
                        enabled = state.isValid && !state.isLoading && !state.isSubmitting
                    )
                }
            }

            // Player Grid Section (Dashboard - No Modal!)
            PlayerGridSection(
                players = state.players,
                selectingCategory = state.selectingCategory,
                currentSlots = state.slots,
                remainingBudget = state.remainingBudget,
                searchQuery = state.searchQuery,
                sortOption = state.sortOption,
                showAffordableOnly = state.showAffordableOnly,
                isMobile = isMobileView,
                onSearchChange = { query ->
                    state = state.copy(searchQuery = query)
                },
                onSortChange = { option ->
                    state = state.copy(sortOption = option)
                },
                onToggleAffordable = {
                    state = state.copy(showAffordableOnly = !state.showAffordableOnly)
                },
                onSelectPlayer = { player ->
                    val category = state.selectingCategory ?: return@PlayerGridSection
                    val newSlots = state.slots.withSlot(category, player.id)
                    val newCost = calculateTotalCost(newSlots, state.players)
                    val newRemaining = state.salaryCap - newCost

                    state = state.copy(
                        slots = newSlots,
                        totalCost = newCost,
                        remainingBudget = newRemaining,
                        isValid = newSlots.isFilled() && newRemaining >= 0,
                        selectingCategory = null,
                        lastAddedPlayer = player.displayName,
                        showSuccessToast = true
                    )
                },
                onViewSkills = { player ->
                    state = state.copy(viewingSkillsPlayer = player)
                }
            )

            // Scoring Reference (at bottom)
            ScoringReferenceSection(contestName = state.contestName)

            // Spacer for mobile fixed button (prevents content from being hidden)
            if (isMobileView) {
                Div({
                    style {
                        height(100.px)
                        property("height", "calc(100px + env(safe-area-inset-bottom, 0px))")
                    }
                })
            }
            }
        }

        // Mobile Fixed Submit Button
        if (isMobileView) {
            MobileFixedSubmitButton(
                filledCount = state.slots.filledCount(),
                isValid = state.isValid,
                isSubmitting = state.isSubmitting,
                isLoading = state.isLoading,
                isEditMode = state.isEditMode,
                remainingBudget = state.remainingBudget,
                onSubmit = {
                    if (state.isValid && !state.isSubmitting) {
                        state = state.copy(showSubmitConfirmation = true)
                    }
                },
                onClearAll = if (state.slots.filledCount() > 0) {
                    {
                        state = state.copy(
                            slots = LineupSlots(),
                            totalCost = 0,
                            remainingBudget = state.salaryCap,
                            isValid = false,
                            selectingCategory = null
                        )
                    }
                } else null
            )
        }

        // Submit Confirmation Dialog
        if (state.showSubmitConfirmation) {
            val filledSlots = listOfNotNull(
                state.slots.serve,
                state.slots.forehand,
                state.slots.backhand,
                state.slots.volleys,
                state.slots.fighting
            ).size

            LineupSubmitConfirmationDialog(
                contestName = state.contestName,
                playerCount = filledSlots,
                totalSalary = state.totalCost,
                salaryCap = state.salaryCap,
                onConfirm = {
                    state = state.copy(showSubmitConfirmation = false, isSubmitting = true, errors = emptyList())
                    scope.launch {
                        val editLineupId = state.editLineupId
                        if (editLineupId != null) {
                            // EDIT MODE: Update existing lineup
                            DependencyContainer.updateLineupUseCase(editLineupId, state.slots).fold(
                                onSuccess = {
                                    state = state.copy(isSubmitting = false)
                                    onSubmit()
                                },
                                onFailure = { error ->
                                    state = state.copy(
                                        isSubmitting = false,
                                        errors = listOf("Failed to update lineup: ${error.message}")
                                    )
                                }
                            )
                        } else {
                            // CREATE MODE: Create entry then lineup
                            DependencyContainer.enterContestUseCase(contestId).fold(
                                onSuccess = { entry ->
                                    DependencyContainer.createLineupUseCase(
                                        contestEntryId = entry.id,
                                        contestId = contestId,
                                        slots = state.slots
                                    ).fold(
                                        onSuccess = {
                                            state = state.copy(isSubmitting = false)
                                            onSubmit()
                                        },
                                        onFailure = { error ->
                                            state = state.copy(
                                                isSubmitting = false,
                                                errors = listOf("Failed to save lineup: ${error.message}")
                                            )
                                        }
                                    )
                                },
                                onFailure = { error ->
                                    state = state.copy(
                                        isSubmitting = false,
                                        errors = listOf("Failed to enter contest: ${error.message}")
                                    )
                                }
                            )
                        }
                    }
                },
                onCancel = {
                    state = state.copy(showSubmitConfirmation = false)
                }
            )
        }

        // Player Skill Drawer
        state.viewingSkillsPlayer?.let { player ->
            val usedPlayerIds = state.slots.getAllPlayerIds().toSet()
            val isAlreadyInLineup = usedPlayerIds.contains(player.id)

            PlayerSkillDrawer(
                player = player,
                selectingCategory = state.selectingCategory,
                remainingBudget = state.remainingBudget,
                isAlreadyInLineup = isAlreadyInLineup,
                onSelect = {
                    // Add player to lineup from drawer
                    val category = state.selectingCategory ?: return@PlayerSkillDrawer
                    val newSlots = state.slots.withSlot(category, player.id)
                    val newCost = calculateTotalCost(newSlots, state.players)
                    val newRemaining = state.salaryCap - newCost

                    state = state.copy(
                        slots = newSlots,
                        totalCost = newCost,
                        remainingBudget = newRemaining,
                        isValid = newSlots.isFilled() && newRemaining >= 0,
                        selectingCategory = null,
                        viewingSkillsPlayer = null,  // Close drawer
                        lastAddedPlayer = player.displayName,
                        showSuccessToast = true
                    )
                },
                onClose = {
                    state = state.copy(viewingSkillsPlayer = null)
                }
            )
        }

    }
}

/**
 * Category Slot Component - Enhanced with selecting state and remove button
 */
@Composable
private fun CategorySlot(
    category: Category,
    player: TennisPlayerEntity?,
    isSelecting: Boolean,
    onClick: () -> Unit,
    onRemove: (() -> Unit)?
) {
    val categoryColor = getCategoryColor(category)

    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(16.px)
            cursor("pointer")
            property("transition", "all 0.2s ease")
            textAlign("center")
            minHeight(200.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            position(Position.Relative)

            if (isSelecting) {
                property("box-shadow", "0 0 0 3px $categoryColor, 0 4px 12px rgba(0, 0, 0, 0.15)")
                property("transform", "scale(1.02)")
            } else {
                property("box-shadow", "0 2px 4px rgba(0, 0, 0, 0.1)")
            }
        }
        onClick { onClick() }
    }) {
        // Remove button (top-right corner when player is selected)
        // Size: 32px for better touch target (44px is ideal, but space constrained)
        if (player != null && onRemove != null) {
            Div({
                style {
                    position(Position.Absolute)
                    top(6.px)
                    right(6.px)
                    width(32.px)
                    height(32.px)
                    borderRadius(50.percent)
                    backgroundColor(Colors.ErrorLight)
                    color(Colors.Error)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    fontSize(16.px)
                    fontWeight(700)
                    cursor("pointer")
                    property("z-index", "1")
                }
                onClick { e ->
                    e.stopPropagation()
                    onRemove()
                }
                title("Remove player")
            }) {
                Text("×")
            }
        }

        // Category Label
        Div({
            style {
                backgroundColor(categoryColor)
                color(Colors.White)
                padding(6.px, 12.px)
                borderRadius(16.px)
                fontSize(11.px)
                fontWeight(600)
                textTransform("uppercase")
                letterSpacing(0.5.px)
                property("align-self", "center")
            }
        }) {
            Text(category.displayName)
        }

        // Player Info or Empty State
        if (player != null) {
            Div({
                style {
                    marginTop(10.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                    flex(1)
                }
            }) {
                // Ranking badge
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(600)
                        color(Colors.Primary)
                        backgroundColor(Colors.PrimaryLight)
                        padding(2.px, 8.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text("#${player.atpRanking ?: "NR"}")
                }

                // Player name
                P({
                    style {
                        fontSize(14.px)
                        fontWeight(600)
                        color(Colors.TextPrimary)
                        margin(2.px, 0.px)
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        maxWidth(100.percent)
                    }
                }) {
                    Text(player.displayName)
                }

                // Country
                Span({
                    style {
                        fontSize(11.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text(player.country)
                }

                // Opponent info
                if (player.opponentName != null) {
                    Div({
                        style {
                            width(30.px)
                            height(1.px)
                            backgroundColor(Colors.Border)
                            margin(6.px, 0.px)
                        }
                    })

                    // Opponent display
                    Span({
                        style {
                            fontSize(10.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text("vs ${formatOpponentNameShort(player.opponentName!!)}")
                    }

                    // Matchup badge
                    MatchupBadge(
                        playerRanking = player.atpRanking,
                        opponentRanking = player.opponentRanking
                    )
                }

                // Cost badge
                Div({
                    style {
                        backgroundColor(categoryColor)
                        color(Colors.White)
                        padding(4.px, 12.px)
                        borderRadius(4.px)
                        fontSize(13.px)
                        fontWeight(600)
                        marginTop(6.px)
                    }
                }) {
                    Text("$${player.getCostForCategory(category)}K")
                }
            }
        } else {
            // Empty state
            Div({
                style {
                    marginTop(12.px)
                    flex(1)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                }
            }) {
                Div({
                    style {
                        padding(16.px, 24.px)
                        border(2.px, LineStyle.Dashed, if (isSelecting) categoryColor else Colors.Border)
                        borderRadius(8.px)
                        backgroundColor(if (isSelecting) Color("rgba(0,0,0,0.02)") else Color("transparent"))
                    }
                }) {
                    P({
                        style {
                            color(if (isSelecting) categoryColor else Colors.TextSecondary)
                            margin(0.px)
                            fontSize(13.px)
                            fontWeight(if (isSelecting) 500 else 400)
                        }
                    }) {
                        Text(if (isSelecting) "Select below" else "Tap to select")
                    }
                }
            }
        }
    }
}

/**
 * Salary Cap Bar Component
 */
@Composable
private fun SalaryCapBar(
    used: Int,
    remaining: Int,
    cap: Int
) {
    val percentage = (used.toFloat() / cap * 100).coerceIn(0f, 100f)
    val isOverBudget = remaining < 0

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        Span({
            style {
                color(Colors.White)
                fontSize(14.px)
            }
        }) {
            Text("Budget:")
        }

        Div({
            style {
                width(200.px)
                height(12.px)
                backgroundColor(Color("rgba(255, 255, 255, 0.3)"))
                borderRadius(6.px)
                overflow("hidden")
            }
        }) {
            Div({
                style {
                    width(percentage.percent)
                    height(100.percent)
                    backgroundColor(if (isOverBudget) Colors.Error else Colors.Secondary)
                    borderRadius(6.px)
                    property("transition", "width 0.3s ease")
                }
            })
        }

        Span({
            style {
                color(if (isOverBudget) Colors.ErrorLight else Colors.White)
                fontSize(14.px)
                fontWeight(600)
            }
        }) {
            Text("$${used}K / $${cap}K ($${remaining}K left)")
        }
    }
}

/**
 * Player Grid Section - Dashboard layout with all players visible
 * Replaces the old modal-based player picker
 */
@Composable
private fun PlayerGridSection(
    players: List<TennisPlayerEntity>,
    selectingCategory: Category?,
    currentSlots: LineupSlots,
    remainingBudget: Int,
    searchQuery: String,
    sortOption: PlayerSortOption,
    showAffordableOnly: Boolean,
    isMobile: Boolean,
    onSearchChange: (String) -> Unit,
    onSortChange: (PlayerSortOption) -> Unit,
    onToggleAffordable: () -> Unit,
    onSelectPlayer: (TennisPlayerEntity) -> Unit,
    onViewSkills: (TennisPlayerEntity) -> Unit
) {
    val usedPlayerIds = currentSlots.getAllPlayerIds().toSet()
    val isSelectingMode = selectingCategory != null

    // Filter and sort players
    val filteredPlayers = remember(players, searchQuery, sortOption, showAffordableOnly, remainingBudget, usedPlayerIds, selectingCategory) {
        players
            .filter { it.isActive }
            // Search filter
            .filter { player ->
                searchQuery.isBlank() ||
                player.displayName.contains(searchQuery, ignoreCase = true) ||
                player.country.contains(searchQuery, ignoreCase = true)
            }
            // Hide unaffordable if toggle is on and in selecting mode
            .filter { player ->
                if (!showAffordableOnly || selectingCategory == null) true
                else {
                    val cost = player.getCostForCategory(selectingCategory)
                    val isUsed = usedPlayerIds.contains(player.id)
                    !isUsed && cost <= remainingBudget
                }
            }
            // Sort
            .sortedWith(when (sortOption) {
                PlayerSortOption.COST_LOW -> compareBy {
                    selectingCategory?.let { cat -> it.getCostForCategory(cat) } ?: it.costs.serve
                }
                PlayerSortOption.COST_HIGH -> compareByDescending {
                    selectingCategory?.let { cat -> it.getCostForCategory(cat) } ?: it.costs.serve
                }
                PlayerSortOption.RANKING -> compareBy { it.atpRanking ?: Int.MAX_VALUE }
                PlayerSortOption.MATCHUP_EASY -> compareByDescending {
                    val playerRank = it.atpRanking
                    val opponentRank = it.opponentRanking
                    if (playerRank != null && opponentRank != null) {
                        opponentRank - playerRank
                    } else {
                        Int.MIN_VALUE
                    }
                }
                PlayerSortOption.BEST_VALUE -> compareByDescending {
                    val cost = selectingCategory?.let { cat -> it.getCostForCategory(cat) } ?: it.costs.serve
                    val ranking = it.atpRanking ?: 500
                    val rankingFactor = minOf(ranking, 100).toDouble() / 100.0
                    (1.0 - rankingFactor) / cost.toDouble()
                }
            })
    }

    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(if (isMobile) 16.px else 20.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 4px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Section Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(if (isMobile) 12.px else 16.px)
                flexWrap(FlexWrap.Wrap)
                gap(if (isMobile) 8.px else 12.px)
            }
        }) {
            H3({
                style {
                    fontSize(if (isMobile) 16.px else 18.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                    margin(0.px)
                }
            }) {
                Text(if (isSelectingMode) "Select a Player" else "Available Players")
            }

            // Player count
            Span({
                style {
                    fontSize(if (isMobile) 12.px else 13.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("${filteredPlayers.size} player${if (filteredPlayers.size != 1) "s" else ""}")
            }
        }

        // Search and Filters Row
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(if (isMobile) 8.px else 12.px)
                marginBottom(if (isMobile) 12.px else 16.px)
                flexWrap(FlexWrap.Wrap)
                alignItems(AlignItems.Center)
            }
        }) {
            // Search Input
            Div({
                style {
                    position(Position.Relative)
                    flex(1)
                    // Allow search to be full width on mobile
                    minWidth(if (isMobile) 100.percent else 200.px)
                }
            }) {
                Input(InputType.Text) {
                    value(searchQuery)
                    placeholder("Search by name or country...")
                    onInput { event ->
                        val value = (event.target as? HTMLInputElement)?.value ?: ""
                        onSearchChange(value)
                    }
                    style {
                        width(100.percent)
                        padding(10.px, 36.px, 10.px, 12.px)
                        fontSize(14.px)
                        borderRadius(8.px)
                        border(1.px, LineStyle.Solid, Colors.Border)
                        backgroundColor(Colors.Background)
                        color(Colors.TextPrimary)
                        property("box-sizing", "border-box")
                        property("outline", "none")
                    }
                }
                if (searchQuery.isNotEmpty()) {
                    Span({
                        style {
                            position(Position.Absolute)
                            right(10.px)
                            top(50.percent)
                            property("transform", "translateY(-50%)")
                            cursor("pointer")
                            color(Colors.TextSecondary)
                            fontSize(14.px)
                        }
                        onClick { onSearchChange("") }
                    }) {
                        Text("✕")
                    }
                }
            }

            // Sort Dropdown
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                }
            }) {
                Span({
                    style {
                        fontSize(13.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("Sort:")
                }
                Select({
                    onChange { event ->
                        val value = (event.target as? org.w3c.dom.HTMLSelectElement)?.value
                        val option = PlayerSortOption.entries.find { it.name == value } ?: PlayerSortOption.BEST_VALUE
                        onSortChange(option)
                    }
                    style {
                        padding(8.px, 12.px)
                        borderRadius(6.px)
                        border(1.px, LineStyle.Solid, Colors.Border)
                        fontSize(13.px)
                        cursor("pointer")
                        backgroundColor(Colors.Background)
                        color(Colors.TextPrimary)
                    }
                }) {
                    PlayerSortOption.entries.forEach { option ->
                        Option(value = option.name, attrs = {
                            if (option == sortOption) attr("selected", "")
                        }) {
                            Text(option.label)
                        }
                    }
                }
            }

            // Show Affordable Only Toggle (only relevant when selecting)
            if (isSelectingMode) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                        cursor("pointer")
                    }
                    onClick { onToggleAffordable() }
                }) {
                    Input(InputType.Checkbox) {
                        checked(showAffordableOnly)
                        style {
                            cursor("pointer")
                        }
                    }
                    Span({
                        style {
                            fontSize(13.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text("Hide unavailable")
                    }
                }
            }
        }

        // Player Grid
        if (filteredPlayers.isEmpty()) {
            // Empty State
            Div({
                style {
                    textAlign("center")
                    padding(48.px, 24.px)
                    color(Colors.TextSecondary)
                }
            }) {
                P({
                    style {
                        fontSize(16.px)
                        margin(0.px, 0.px, 12.px, 0.px)
                    }
                }) {
                    Text("No players match your search")
                }
                if (searchQuery.isNotEmpty()) {
                    Span({
                        style {
                            fontSize(14.px)
                            color(Colors.Primary)
                            cursor("pointer")
                            textDecoration("underline")
                        }
                        onClick { onSearchChange("") }
                    }) {
                        Text("Clear search")
                    }
                }
            }
        } else {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    // Mobile: single column, Desktop: auto-fill with 300px min
                    property(
                        "grid-template-columns",
                        if (isMobile) "1fr" else "repeat(auto-fill, minmax(min(100%, 300px), 1fr))"
                    )
                    gap(if (isMobile) 12.px else 16.px)
                }
                classes("player-grid")
            }) {
                filteredPlayers.forEach { player ->
                    val isAlreadyInLineup = usedPlayerIds.contains(player.id)

                    PlayerCard(
                        player = player,
                        selectingCategory = selectingCategory,
                        isAlreadyInLineup = isAlreadyInLineup,
                        remainingBudget = remainingBudget,
                        onSelect = { onSelectPlayer(player) },
                        onViewSkills = { onViewSkills(player) }
                    )
                }
            }
        }
    }
}

/**
 * Format opponent name to be more compact (first initial + last name)
 */
private fun formatOpponentName(name: String): String {
    val parts = name.split(" ")
    return if (parts.size >= 2) {
        "${parts.first().first()}. ${parts.drop(1).joinToString(" ")}"
    } else {
        name
    }
}

/**
 * Format opponent name for very tight spaces (just last name, truncated if needed)
 */
private fun formatOpponentNameShort(name: String): String {
    val parts = name.split(" ")
    val lastName = if (parts.size >= 2) parts.last() else name
    // Truncate if longer than 10 chars
    return if (lastName.length > 10) lastName.take(9) + "…" else lastName
}

/**
 * Format match time from ISO string to readable format
 */
private fun formatMatchTime(isoTime: String?): String {
    if (isoTime == null) return ""
    return try {
        if (isoTime.contains("T")) {
            val timePart = isoTime.substringAfter("T").substringBefore("Z").substringBefore("+")
            val hourMinute = timePart.split(":").take(2)
            if (hourMinute.size >= 2) {
                val hour = hourMinute[0].toIntOrNull() ?: return isoTime
                val minute = hourMinute[1]
                val amPm = if (hour >= 12) "PM" else "AM"
                val hour12 = when {
                    hour == 0 -> 12
                    hour > 12 -> hour - 12
                    else -> hour
                }
                "$hour12:$minute $amPm"
            } else {
                isoTime
            }
        } else {
            isoTime
        }
    } catch (e: Exception) {
        isoTime
    }
}

/**
 * Calculate total cost for a lineup
 */
private fun calculateTotalCost(slots: LineupSlots, players: List<TennisPlayerEntity>): Int {
    var total = 0

    slots.serve?.let { id ->
        players.find { it.id == id }?.let { total += it.costs.serve }
    }
    slots.forehand?.let { id ->
        players.find { it.id == id }?.let { total += it.costs.forehand }
    }
    slots.backhand?.let { id ->
        players.find { it.id == id }?.let { total += it.costs.backhand }
    }
    slots.volleys?.let { id ->
        players.find { it.id == id }?.let { total += it.costs.volleys }
    }
    slots.fighting?.let { id ->
        players.find { it.id == id }?.let { total += it.costs.fighting }
    }

    return total
}

/**
 * Get color for a category
 */
private fun getCategoryColor(category: Category): CSSColorValue {
    return when (category) {
        Category.SERVE -> Colors.Category.Serve
        Category.FOREHAND -> Colors.Category.Forehand
        Category.BACKHAND -> Colors.Category.Backhand
        Category.VOLLEYS -> Colors.Category.Volleys
        Category.FIGHTING -> Colors.Category.Fighting
    }
}

/**
 * Determines if a contest is for a Grand Slam tournament (best-of-5 for men's matches).
 */
private fun isGrandSlamContest(contestName: String): Boolean {
    val name = contestName.lowercase()
    return name.contains("australian open") ||
            name.contains("french open") ||
            name.contains("roland garros") ||
            name.contains("wimbledon") ||
            name.contains("us open")
}

/**
 * Scoring Reference Section - compact display of scoring rules
 */
@Composable
private fun ScoringReferenceSection(contestName: String) {
    var isExpanded by remember { mutableStateOf(true) }
    val isBestOfFive = isGrandSlamContest(contestName)

    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            marginBottom(24.px)
            overflow("hidden")
            property("box-shadow", "0 2px 4px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Header (clickable)
        Div({
            style {
                padding(16.px, 20.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                cursor("pointer")
                backgroundColor(if (isExpanded) Colors.SurfaceVariant else Colors.Surface)
            }
            onClick { isExpanded = !isExpanded }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(14.px)
                        fontWeight(600)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text("Scoring Reference")
                }
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("(How points are earned)")
                }
            }

            Span({
                style {
                    fontSize(16.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text(if (isExpanded) "−" else "+")
            }
        }

        // Expandable content
        if (isExpanded) {
            Div({
                style {
                    padding(20.px)
                }
            }) {
                // Compact grid of scoring categories
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                        gap(12.px)
                    }
                }) {
                    ScoringReferenceItem(
                        category = "Serve",
                        color = Colors.Category.Serve,
                        scoring = "Ace +3 • Double Fault -1"
                    )
                    ScoringReferenceItem(
                        category = "Forehand",
                        color = Colors.Category.Forehand,
                        scoring = "Winner +2 • Error -1"
                    )
                    ScoringReferenceItem(
                        category = "Backhand",
                        color = Colors.Category.Backhand,
                        scoring = "Winner +2 • Error -1"
                    )
                    ScoringReferenceItem(
                        category = "Volleys",
                        color = Colors.Category.Volleys,
                        scoring = "Winner +3"
                    )
                    ScoringReferenceItem(
                        category = "Fighting",
                        color = Colors.Category.Fighting,
                        scoring = "Break Conv. +4 • Break Saved +2"
                    )
                }

                // Quick finish bonus
                Div({
                    style {
                        marginTop(12.px)
                        paddingTop(12.px)
                        property("border-top", "1px solid ${Colors.BorderLight}")
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        gap(16.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(12.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text("⚡ Quick Finish Bonus:")
                    }
                    if (isBestOfFive) {
                        // Best-of-5 (Grand Slams)
                        Span({
                            style {
                                fontSize(12.px)
                                fontWeight(500)
                                color(Colors.Primary)
                            }
                        }) {
                            Text("3 sets +20")
                        }
                        Span({
                            style {
                                fontSize(12.px)
                                color(Colors.TextSecondary)
                            }
                        }) {
                            Text("•")
                        }
                        Span({
                            style {
                                fontSize(12.px)
                                fontWeight(500)
                                color(Colors.Primary)
                            }
                        }) {
                            Text("4 sets +10")
                        }
                        Span({
                            style {
                                fontSize(12.px)
                                color(Colors.TextSecondary)
                            }
                        }) {
                            Text("•")
                        }
                        Span({
                            style {
                                fontSize(12.px)
                                fontWeight(500)
                                color(Colors.TextSecondary)
                            }
                        }) {
                            Text("5 sets +0")
                        }
                    } else {
                        // Best-of-3 (most tournaments)
                        Span({
                            style {
                                fontSize(12.px)
                                fontWeight(500)
                                color(Colors.Primary)
                            }
                        }) {
                            Text("2 sets +10")
                        }
                    }
                }
            }
        }
    }
}

/**
 * Individual scoring reference item
 */
@Composable
private fun ScoringReferenceItem(
    category: String,
    color: CSSColorValue,
    scoring: String
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            padding(8.px, 12.px)
            backgroundColor(Colors.SurfaceVariant)
            borderRadius(6.px)
        }
    }) {
        // Category color indicator
        Div({
            style {
                width(4.px)
                height(24.px)
                backgroundColor(color)
                borderRadius(2.px)
            }
        })

        Div {
            Span({
                style {
                    fontSize(12.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                    display(DisplayStyle.Block)
                }
            }) {
                Text(category)
            }
            Span({
                style {
                    fontSize(11.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text(scoring)
            }
        }
    }
}

/**
 * Mobile fixed submit button at the bottom of the screen.
 * Includes iOS safe area support.
 */
@Composable
private fun MobileFixedSubmitButton(
    filledCount: Int,
    isValid: Boolean,
    isSubmitting: Boolean,
    isLoading: Boolean,
    isEditMode: Boolean,
    remainingBudget: Int,
    onSubmit: () -> Unit,
    onClearAll: (() -> Unit)?
) {
    val isOverBudget = remainingBudget < 0
    val canSubmit = isValid && !isLoading && !isSubmitting

    Div({
        style {
            position(Position.Fixed)
            bottom(0.px)
            left(0.px)
            right(0.px)
            backgroundColor(Colors.Surface)
            property("box-shadow", "0 -2px 10px rgba(0, 0, 0, 0.1)")
            property("z-index", "100")
            padding(12.px, 16.px)
            property("padding-bottom", "calc(12px + env(safe-area-inset-bottom, 0px))")
        }
    }) {
        // Row with Clear All and Submit buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
                alignItems(AlignItems.Center)
            }
        }) {
            // Clear All button (optional, only when slots are filled)
            if (onClearAll != null) {
                Div({
                    style {
                        padding(14.px, 16.px)
                        borderRadius(10.px)
                        fontSize(14.px)
                        fontWeight(600)
                        cursor("pointer")
                        backgroundColor(Colors.SurfaceVariant)
                        color(Colors.TextSecondary)
                        border(1.px, LineStyle.Solid, Colors.Border)
                        minHeight(48.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                    }
                    onClick { onClearAll() }
                }) {
                    Text("Clear")
                }
            }

            // Main Submit button
            Div({
                style {
                    flex(1)
                    backgroundColor(
                        when {
                            isSubmitting -> Colors.Primary
                            canSubmit -> Colors.Primary
                            isOverBudget -> Colors.Error
                            else -> Colors.SurfaceVariant
                        }
                    )
                    color(
                        when {
                            canSubmit || isSubmitting || isOverBudget -> Colors.White
                            else -> Colors.TextSecondary
                        }
                    )
                    padding(14.px, 20.px)
                    borderRadius(10.px)
                    fontSize(16.px)
                    fontWeight(700)
                    textAlign("center")
                    cursor(if (canSubmit) "pointer" else "default")
                    if (canSubmit) {
                        property("box-shadow", "0 4px 12px rgba(0, 166, 81, 0.3)")
                    }
                    property("transition", "transform 0.2s, box-shadow 0.2s, background-color 0.2s")
                    minHeight(48.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
                if (canSubmit) {
                    onClick { onSubmit() }
                }
            }) {
                Text(
                    when {
                        isSubmitting -> if (isEditMode) "Updating..." else "Submitting..."
                        isValid -> if (isEditMode) "Update Lineup" else "Submit Lineup"
                        isOverBudget -> "Over Budget by $${-remainingBudget}K"
                        filledCount < 5 -> "Select Players ($filledCount/5)"
                        else -> if (isEditMode) "Update Lineup" else "Submit Lineup"
                    }
                )
            }
        }
    }
}

/**
 * First-time user guide banner - shows step-by-step instructions
 * for building their first lineup
 */
@Composable
private fun FirstTimeUserGuideBanner(
    step: GuidedTourStep,
    currentStepIndex: Int,
    totalSteps: Int,
    onNext: () -> Unit,
    onSkip: () -> Unit
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(16.px, 20.px)
            marginBottom(20.px)
            property("box-shadow", "0 4px 16px rgba(0, 0, 0, 0.1)")
            step.accentColor?.let {
                property("border-left", "4px solid $it")
            }
            property("animation", "tooltipFadeIn 0.3s ease-out")
        }
    }) {
        // Header row
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(8.px)
            }
        }) {
            // Step indicator
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(600)
                        color(Colors.White)
                        backgroundColor(Colors.Primary)
                        padding(4.px, 10.px)
                        borderRadius(12.px)
                        property("text-transform", "uppercase")
                        letterSpacing(0.5.px)
                    }
                }) {
                    Text("First Lineup Guide")
                }
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("$currentStepIndex of $totalSteps")
                }
            }

            // Skip link
            Span({
                style {
                    fontSize(13.px)
                    color(Colors.TextSecondary)
                    cursor("pointer")
                    padding(4.px, 8.px)
                    borderRadius(4.px)
                }
                onClick { onSkip() }
                onMouseOver {
                    (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor =
                        Colors.SurfaceVariant.toString()
                }
                onMouseOut {
                    (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor =
                        "transparent"
                }
            }) {
                Text("Skip guide")
            }
        }

        // Title
        Div({
            style {
                fontSize(16.px)
                fontWeight(700)
                color(Colors.TextPrimary)
                marginBottom(4.px)
            }
        }) {
            Text(step.title)
        }

        // Description
        P({
            style {
                fontSize(14.px)
                color(Colors.TextSecondary)
                lineHeight("1.5")
                margin(0.px, 0.px, 12.px, 0.px)
            }
        }) {
            Text(step.description)
        }

        // Progress dots and next button
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            // Progress dots
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(6.px)
                }
            }) {
                repeat(totalSteps) { index ->
                    Div({
                        style {
                            width(if (index + 1 == currentStepIndex) 20.px else 8.px)
                            height(8.px)
                            borderRadius(4.px)
                            backgroundColor(
                                if (index + 1 == currentStepIndex)
                                    Colors.Primary
                                else if (index + 1 < currentStepIndex)
                                    Colors.Success
                                else
                                    Colors.Border
                            )
                            property("transition", "all 0.3s ease")
                        }
                    })
                }
            }

            // Next button
            Button(
                text = if (currentStepIndex == totalSteps) "Got it!" else "Next →",
                onClick = onNext,
                variant = "primary"
            )
        }
    }
}
